import React, { useMemo } from 'react';
import { Plus, Trash2 } from 'lucide-react';

function toDateStr(d) {
  if (!d) return '';
  return String(d).split('T')[0];
}

function addDays(dateStr, days) {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
}

/**
 * V2 Section D — contiguous allocation ranges summing to 100%.
 */
export function RevenueAllocationRangeBuilder({
  serviceFrom,
  serviceTo,
  totalAmount,
  ranges,
  setRanges,
}) {
  const pctTotal = useMemo(
    () => ranges.reduce((s, r) => s + parseFloat(r.allocation_percentage || 0), 0),
    [ranges]
  );

  const updateRange = (idx, field, value) => {
    setRanges(prev => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };

  const addRange = () => {
    const last = ranges[ranges.length - 1];
    const start = last
      ? addDays(toDateStr(last.range_end_date), 1)
      : toDateStr(serviceFrom);
    setRanges(prev => [
      ...prev,
      {
        range_start_date: start,
        range_end_date: toDateStr(serviceTo),
        allocation_percentage: Math.max(0, 100 - pctTotal).toFixed(2),
        sequence_order: prev.length + 1,
      },
    ]);
  };

  const removeRange = (idx) => {
    if (ranges.length <= 2) return;
    setRanges(prev => prev.filter((_, i) => i !== idx).map((r, i) => ({ ...r, sequence_order: i + 1 })));
  };

  const validation = useMemo(() => {
    if (!serviceFrom || !serviceTo) return 'Set service period dates first';
    if (ranges.length < 2) return 'Add at least 2 ranges';
    if (Math.abs(pctTotal - 100) > 0.01) return `Percentages must sum to 100% (currently ${pctTotal.toFixed(2)}%)`;
    const sorted = [...ranges].sort((a, b) => a.sequence_order - b.sequence_order);
    if (toDateStr(sorted[0].range_start_date) !== toDateStr(serviceFrom)) {
      return 'First range must start on service period start';
    }
    if (toDateStr(sorted[sorted.length - 1].range_end_date) !== toDateStr(serviceTo)) {
      return 'Last range must end on service period end';
    }
    for (let i = 1; i < sorted.length; i++) {
      const expected = addDays(toDateStr(sorted[i - 1].range_end_date), 1);
      if (toDateStr(sorted[i].range_start_date) !== expected) {
        return `Gap between range ${i} and ${i + 1} — ranges must be contiguous`;
      }
    }
    return null;
  }, [ranges, serviceFrom, serviceTo, pctTotal]);

  return (
    <div className="space-y-3 border border-gray-200 rounded-xl p-4">
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium text-gray-700">Custom period allocation</div>
        <button type="button" className="btn-secondary text-xs py-1 px-2" onClick={addRange}>
          <Plus size={14} className="inline mr-1" /> Add range
        </button>
      </div>

      {ranges.map((r, idx) => (
        <div key={idx} className="grid grid-cols-12 gap-2 items-end">
          <div className="col-span-3">
            <label className="text-xs text-gray-500">Start</label>
            <input type="date" className="input text-sm" value={toDateStr(r.range_start_date)}
              onChange={e => updateRange(idx, 'range_start_date', e.target.value)} />
          </div>
          <div className="col-span-3">
            <label className="text-xs text-gray-500">End</label>
            <input type="date" className="input text-sm" value={toDateStr(r.range_end_date)}
              onChange={e => updateRange(idx, 'range_end_date', e.target.value)} />
          </div>
          <div className="col-span-2">
            <label className="text-xs text-gray-500">%</label>
            <input type="number" step="0.01" className="input text-sm"
              value={r.allocation_percentage}
              onChange={e => updateRange(idx, 'allocation_percentage', e.target.value)} />
          </div>
          <div className="col-span-3 text-sm text-gray-600 pb-2">
            ≈ {totalAmount
              ? ((parseFloat(r.allocation_percentage || 0) / 100) * parseFloat(totalAmount)).toLocaleString(undefined, { maximumFractionDigits: 2 })
              : '—'}
          </div>
          <div className="col-span-1 pb-1">
            <button type="button" disabled={ranges.length <= 2}
              className="text-red-500 disabled:opacity-30" onClick={() => removeRange(idx)}>
              <Trash2 size={16} />
            </button>
          </div>
        </div>
      ))}

      <div className="flex justify-between text-sm">
        <span className={Math.abs(pctTotal - 100) < 0.01 ? 'text-green-700' : 'text-amber-700'}>
          Total: {pctTotal.toFixed(2)}%
        </span>
        {validation && <span className="text-amber-600">{validation}</span>}
      </div>
    </div>
  );
}

export function isAllocationValid(serviceFrom, serviceTo, ranges) {
  if (!serviceFrom || !serviceTo || ranges.length < 2) return false;
  const pctTotal = ranges.reduce((s, r) => s + parseFloat(r.allocation_percentage || 0), 0);
  if (Math.abs(pctTotal - 100) > 0.01) return false;
  const sorted = [...ranges].sort((a, b) => (a.sequence_order || 0) - (b.sequence_order || 0));
  if (toDateStr(sorted[0].range_start_date) !== toDateStr(serviceFrom)) return false;
  if (toDateStr(sorted[sorted.length - 1].range_end_date) !== toDateStr(serviceTo)) return false;
  for (let i = 1; i < sorted.length; i++) {
    if (toDateStr(sorted[i].range_start_date) !== addDays(toDateStr(sorted[i - 1].range_end_date), 1)) {
      return false;
    }
  }
  return true;
}

export default RevenueAllocationRangeBuilder;
